import React from "react";
import OkrAlignmentTable from "./okr-alignment-table";
import GoalServices from "../../services/goal.services";
import PublisherComp from "../publisher/publisher";
import { ButtonsComp } from "../inputs/buttons.component";
import { htmlEntities } from '../../utils/common';
import HelpTextComp from '../helper/helptext.component';
import { GoalBoxComp } from './alignment/goal.box';
import ReactDOM from 'react-dom';
import { hot } from "react-hot-loader/root";
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import AlignmentFilter from './okr_sub_components/alignmentFilters.component';
import { okrDetailConstants } from '../../constants/okr/okrDetails.constants';
import { ALIGNMENT_FILTER_EX_DATE, ALIGNMENT_FILTER_STATUS } from '../../constants/okr/alignmentFilter.constants';
import { isTimePeriodFilterApplied } from '../../utils/timePeriodFilter.util';

const OKR_ALIGNMENT_ACTIVE_TAB_KEY = 'alignment-tab'; // for saving current tab in local store
const ALIGNMENT_FILTERS_KEY = 'alignment-filters';

// API goal status values for getFullAlignmentReport endpoint
const API_GOAL_STATUS = {
  ACTIVE_ONLY: okrDetailConstants.GOAL_ACTIVE,  // 1 - Open/Active OKRs only
  CLOSED_ONLY: okrDetailConstants.GOAL_CLOSED,  // 2 - Closed OKRs only
  ALL: okrDetailConstants.GOAL_ALL              // 3 - Both Active and Closed OKRs
};

const getAlignmentFilters = () => {
  const alignmentFilters = localStorage.getItem(`${ALIGNMENT_FILTERS_KEY}-${uData?.org_id}-${uData?.user_id}`);
  return alignmentFilters ? JSON.parse(alignmentFilters) : {};
}

const saveAlignmentFilters = (filters = {}) => {
  localStorage.setItem(`${ALIGNMENT_FILTERS_KEY}-${uData?.org_id}-${uData?.user_id}`, JSON.stringify(filters));
}

const getAlignmentFiltersCount = () => {
  const alignmentFilters = getAlignmentFilters();
  let count = 0;
  if (isTimePeriodFilterApplied({
    startDate: alignmentFilters?.startDate,
    endDate: alignmentFilters?.endDate,
    exDate: alignmentFilters?.exDate,
  })) {
    count++;
  }

  // Status row: count only when the user narrowed beyond default (Open only) and did not clear both chips.
  // "No status" (both false) still applies custom fields / dates but must not add +1 for status.
  if ('openOkr' in alignmentFilters || 'closedOkr' in alignmentFilters) {
    const openOkr = alignmentFilters.openOkr;
    const closedOkr = alignmentFilters.closedOkr;
    const isDefaultOpenOnly = openOkr === true && closedOkr === false;
    const isNoStatusSelected = openOkr === false && closedOkr === false;
    if (!isDefaultOpenOnly && !isNoStatusSelected) {
      count++;
    }
  }

	if (alignmentFilters?.customFields) {
    // Count each custom field filter as one
    try {
      const parsed = JSON.parse(alignmentFilters.customFields);
      count += Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      // Invalid JSON, don't add to count
    }
  }

  return count;
}

// Map saved filter params to backend API format for Full Alignment Report
const mapFiltersToApiParams = (filters = {}) => {
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  // Map frontend status to backend goalStatus (ALIGNMENT_FILTER_STATUS string values match GOAL_* codes)
  let goalStatus = API_GOAL_STATUS.ACTIVE_ONLY;
  // Empty string = user cleared both Open and Closed in the filter UI (not "missing" legacy key)
  if (filters.status === '') {
    goalStatus = API_GOAL_STATUS.ALL;
  } else if (filters.status === ALIGNMENT_FILTER_STATUS.CLOSED) {
    goalStatus = API_GOAL_STATUS.CLOSED_ONLY;
  } else if (filters.status === ALIGNMENT_FILTER_STATUS.BOTH || (filters.openOkr && filters.closedOkr)) {
    goalStatus = API_GOAL_STATUS.ALL;
  }

  return {
    goalStatus,
    startDate: filters.startDate || '',
    endDate: filters.endDate || '',
    exDate: filters.exDate || ALIGNMENT_FILTER_EX_DATE.RANGE,
    customFields: filters.customFields
  };
}

const sortTree = (arr) => {
  arr.sort((a, b) =>
    a.key === '-1'
      ? 1 // 'Add alignment' entry - go last
      :b.key === '-1'
        ? -1 // 'Add alignment' entry - go last
        : a.label && b.label && a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1); // sort A-Z
  arr?.forEach((node) => node.values ? sortTree(node.values) : '');
}

// Build fullAlignFilterParams with teamId/userId when a team or user was previously selected (persisted)
const buildFullAlignFilterParams = (filters = {}, opts = {}) => {
  const base = mapFiltersToApiParams(filters) || {
    goalStatus: API_GOAL_STATUS.ACTIVE_ONLY,
    startDate: '',
    endDate: '',
    exDate: ALIGNMENT_FILTER_EX_DATE.RANGE,
    customFields: filters?.customFields
  };
  // Use !== undefined so explicit null in opts clears scope (?? would fall through to filters)
  const teamId = opts.teamId !== undefined ? opts.teamId : filters?.teamId;
  const userId = opts.userId !== undefined ? opts.userId : filters?.userId;
  const out = {
    ...base,
    ...(teamId ? { teamId } : {}),
    ...(userId ? { userId } : {})
  };
  if (opts._timestamp) out._timestamp = Date.now();
  return out;
};

class OkrAlignmentComp extends React.Component {
	constructor(props) {
		super(props);
		const savedActiveTab = Number.parseInt(localStorage.getItem(`${OKR_ALIGNMENT_ACTIVE_TAB_KEY}-${uData?.org_id}-${uData?.user_id}`), 10);
		const tabsRange = [0, 1, 2];
		const defaultTab = tabsRange.includes(savedActiveTab) ? savedActiveTab : 0;
		const savedFilters = getAlignmentFilters();
		const {t} = this.props;
		this.state = {
			activeButton: "",
			expandText: true,
			filterValue: '1',
			fullAlignData: {},
			showPubisher: false,
			pagination: true,
			searchParams: {},
			offsetValue: 0,
			activeTab: this.props.activeTab || defaultTab,
			showOneTimeMessage: this.props.showOneTimeMessage || false,
			showPersonalObj: false,
			triggerSearch: null,
		 	t: this.props.t,
			openAlignmentFilter: false,
      teamId: savedFilters.teamId ?? null,
      userId: savedFilters.userId ?? null,
			filterDateArray: [{name: 'All time'}], // default until data from API is loaded
			fullAlignFilterParams: buildFullAlignFilterParams(savedFilters)
		};

		this.buttonClick = this.buttonClick.bind(this);
		this.loadPeopleData = this.loadPeopleData.bind(this);
		this.expandSearchText = this.expandSearchText.bind(this);
		this.registerAutocomplete = this.registerAutocomplete.bind(this);
		this.addExistingGoal = this.addExistingGoal.bind(this);
		this.addNewGoal = this.addNewGoal.bind(this);
		this.goalListData = this.goalListData.bind(this);
		this.clickPeopleAlign = this.clickPeopleAlign.bind(this);
		this.loadGoalAlignData = this.loadGoalAlignData.bind(this);
		this.updateOKRData = this.updateOKRData.bind(this);
		this.hideSearchPeople = this.hideSearchPeople.bind(this);
		this.getButtons = this.getButtons.bind(this);
		this.closeHelp = this.closeHelp.bind(this);
		this.tabClick = this.tabClick.bind(this);
		this.setExpandText = this.setExpandText.bind(this);

		this.GoalServices = new GoalServices();
		this.OkrAlignmentTable = React.createRef();
		this.filterButtonRef = React.createRef();
		this.getAlignmentPrintQueryParams = this.getAlignmentPrintQueryParams.bind(this);
		this.teamsData;
		this.buttons = [
			{
				id: "button-teams",
				label: t("Teams"),
				iconClass: "icon icon-outline-people-24px",
				iconActiveClass: "icon icon-outline-people-24px-blue",
				buttonWrapperClass: "",
				buttonClick: this.buttonClick
			},
			{
				id: "button-people",
				label: t("People"),
				iconClass: "icon icon-outline-person_outline-24px",
				iconActiveClass: "icon icon-outline-person_outline-24px-blue",
				buttonWrapperClass: "",
				buttonClick: this.buttonClick
			}
		];
		this.tabs = [
			{
				id: "people",
				label: t("By Organization"),
				iconClass: "icon icon-outline-language-18px",
				buttonWrapperClass: "",
				buttonClick: this.buttonClick
			},
			{
				id: "goal",
				label: `${this.state.t('By Objective')}`,
				iconClass: "icon icon-outline-bar_chart-24px",
				buttonWrapperClass: "",
				buttonClick: this.buttonClick
			},
			{
				id: "fullalign",
				label: t("Full Alignment Report"),
				iconClass: "icon icon-outline-import_export-18px",
				buttonWrapperClass: "",
				buttonClick: this.buttonClick
			}
		];

		this.selectedUserId = this.props.selectedUserId;

		this.teams = this.props.teamTree;

		this.popleJson = this.props.popleJson || [];
		this.selectedUser = this.props.selectedUser;
		this.closeOneTimeMessage = this.closeOneTimeMessage.bind(this);
		this.renderGoalBox = this.renderGoalBox.bind(this);
		this.selectedTeam = this.props.selectedTeam;
		this.openAlignmentFilter = this.openAlignmentFilter.bind(this);
		this.handleFilterApply = this.handleFilterApply.bind(this);
	}

	handleFilterApply(params) {
		// Keep previously used team/user unless explicitly cleared (e.g. by Reset)
		const teamId = params.teamId !== undefined ? params.teamId : this.state.teamId;
		const userId = params.userId !== undefined ? params.userId : this.state.userId;
		const scopeClearedInParams =
			params &&
			Object.prototype.hasOwnProperty.call(params, 'userId') &&
			params.userId === null &&
			Object.prototype.hasOwnProperty.call(params, 'teamId') &&
			params.teamId === null;
		this.setState({ teamId, userId }, () => {
			if (this.state.activeTab == '1') {
				this.loadGoalAlignData({ ...params, teamId, userId });
			} else if (this.state.activeTab == '2') {
				const mappedParams = buildFullAlignFilterParams(params, { teamId, userId, _timestamp: true });
				this.setState({ fullAlignFilterParams: mappedParams }, () => {
					// Reset clears persisted team/user but table searchParams can still hold a stale user;
					// refetch so heading and rows match cleared scope (componentDidUpdate may not run if filter object shape is unchanged).
					if (scopeClearedInParams) {
						const tref = this.OkrAlignmentTable && this.OkrAlignmentTable.current;
						if (tref && typeof tref.searchFullAlignData === 'function') {
							tref.searchFullAlignData(tref.buildAlignRequestParams());
						}
					}
				});
			}
		});
	}

	openAlignmentFilter(e) {
		if (e) {
      e.preventDefault();
    }
		this.setState({
			openAlignmentFilter: !this.state.openAlignmentFilter,
		});
	}

	closeOneTimeMessage() {
		this.setState({ showOneTimeMessage: false }, () => {
			this.GoalServices.closeOneTimeMessage();
		});
		this.teams = this.props.teamTree;
	}

	renderGoalBox(data = {}) {
		let e = document.querySelector('#peopleTreePopUp');
		if (e) {
			data.clickPeopleAlign = this.clickPeopleAlign;
			ReactDOM.render(<GoalBoxComp {...data} />, e);
		}
	}

	getButtons() {
		const activeButton = this.state.activeButton;
		return this.buttons.map(b => {
			b.buttonWrapperClass = activeButton == b.id ? " active " : "";
			return b;
		});
	}

	getAlignmentPrintQueryParams() {
		const table = this.OkrAlignmentTable && this.OkrAlignmentTable.current;
		const fromTable =
			table && typeof table.getLastSearchParams === 'function'
				? table.getLastSearchParams()
				: {};
		const merged = { ...this.state.fullAlignFilterParams, ...fromTable };
		// Drop scope keys from merge; resolve from parent state so stale searchParams (e.g. after filter reset) cannot drive print/email.
		delete merged.userId;
		delete merged.teamId;
		if (merged.sid) {
			// Goal (or other) sid scope — no team/user
		} else {
			const stTeam = this.state.teamId;
			const stUser = this.state.userId;
			const pageUserId = this.props.selectedUserId ?? this.selectedUserId;
			if (stTeam != null && stTeam !== '') {
				merged.teamId = stTeam;
			} else if (stUser != null && stUser !== '') {
				merged.userId = stUser;
			} else if (pageUserId != null && pageUserId !== '') {
				// Match OkrAlignmentTable, which scopes API calls via selectedUserId from the page context.
				merged.userId = pageUserId;
			} else if (typeof uData !== 'undefined' && uData?.user_id != null && uData.user_id !== '') {
				merged.userId = uData.user_id;
			}
		}
		return merged;
	}

	componentDidMount() {
		// document.addEventListener('okr_full_alignment_report', this.updateOKRData);
		$(document).on("okr_full_alignment_report", {}, this.updateOKRData);
    this.GoalServices.getFilterDate({ fromFilter: 1 }).then(response => {
      this.setState({
        filterDateArray: Object.values(response.data.filterComp?.filter(item => !item.name.includes('Custom')))
      });
    });
		if (this.state.activeTab === 1) {
			this.setState({ activeButton: 'button-teams' }, () => {
				this.loadGoalAlignData(getAlignmentFilters());
			});
		} else {
			// other tabs => this.state.activeTab === [0,2]
			// initiate search
			// Teams json to be retrieved in props and loaded on the first instance
			//if the teams hierarchy isnt there directly load people data and hide the buttons
			if (this.teams) {
				// add condition to set active buttons on availability of teams or leave it empty
				this.setState({ activeButton: 'button-teams' }, () => {
					this.loadTeamsData();
				});
			}
			else {
				this.setState({ activeButton: 'button-people' }, () => {
					this.loadPeopleData();
				});
			}
		}
		this.callRequest = false;
		this._isMounted = true;
		window.addEventListener('scroll', this.delayCallback, false);
	}

	componentWillUnmount() {
		this.callRequest = false;
		this._isMounted = false;
		window.removeEventListener('scroll', this.delayCallback, false);
		this.setVizStyles(false);
	}

	updateOKRData(event, data = {}) {
		// console.log('updateOKRData', data);
		if (
			typeof this.OkrAlignmentTable !== "undefined" &&
			this.OkrAlignmentTable !== null
		) {
			this.OkrAlignmentTable.current["updateGoalData"].apply(null, [data]);
		}
	}

	loadTeamsData() {
		showLoaderWidget($("#content"));
		this.GoalServices.getTeamsData().then((response) => {
			removeLoaderWidget(true);
			this.teamsData = response.data.teamsJson;
			// sort teams tree A-Z
      sortTree(this.teamsData);
			mainGoal(response.data.teamsJson, "teams", [], this.selectedTeam, '', this.renderGoalBox);
		}).catch(err => {
			removeLoaderWidget(true);
			console.log('error:', err);
		});
	}

	loadPeopleData() {
		showLoaderWidget($("#content"));
		let _this = this;
		this.GoalServices.getPeopleJson({orgTree: 1}).then((response) => {
			removeLoaderWidget(true);
			this.popleJson = response.data.popleJson;
      // sort people tree A-Z
      sortTree(response.data.popleJson);
			this.selectedUser = response.data.selectedUser;
			if (typeof peopleRootUser !== 'undefined') {
				peopleRootUser = this.selectedUser;
			}
			mainGoal(this.popleJson, "people", [], this.selectedUser, '', this.renderGoalBox);
		}).catch(err => {
			removeLoaderWidget(true);
			console.log('error:', err);
		});
	}

	reopenNode(openAlignedIds = "") {
		if (openAlignedIds != "") {
			openAlignedIds = openAlignedIds.split(",");
			if (Array.isArray(openAlignedIds) && openAlignedIds.length) {
				$.each(openAlignedIds, function (key, val) {
					var depth = key + 1;
					setTimeout(() => {
						$(
							"g#" + val + '.node[new_id="' + depth + "_" + val + '"]'
						).d3Click();
					}, 100);
				});
			}
		}
	}

	buttonClick(item) {
		ReactDOM.unmountComponentAtNode(document.getElementById('goalAlign'))
		this.refs.okr_search_text.value = '';
		let id = item.id;
		if (id == this.state.activeButton) {
			return;
		}
		if (id == "button-people") {
			this.setState({ activeButton: id }, () => {
				this.loadPeopleData();
			});
		} else if (id == "button-teams") {
			this.setState({ activeButton: id }, () => {
				this.loadTeamsData();
			});
		}
	}

	tabClick(e, index, tab) {
		let element = document.getElementById('goalAlign');
		if (element) {
			ReactDOM.unmountComponentAtNode(element);
		}
		this.refs.okr_search_text.value = '';
		e.preventDefault();
		if (index == this.state.activeTab) return;
		const updateState = {
			activeTab: index
		};
		this.setState(updateState, () => {
			if (this.state.activeTab == 0) {
				this.setState({ openAlignmentFilter: false });
				if (this.teams && this.state.activeButton == 'button-teams') {
					// add method for teams search
					this.loadTeamsData();
				} else {
					this.loadPeopleData();
				}
			} else if (this.state.activeTab == 1) {
				this.setState({ activeButton: 'button-teams' }, () => {
					this.loadGoalAlignData(getAlignmentFilters());
				});
			} else if (this.state.activeTab == 2) {
				// Use persisted filters (including customFields) so Full Alignment report respects them
				const saved = getAlignmentFilters();
				this.setState({
					activeButton: 'button-teams',
					openAlignmentFilter: false,
					fullAlignFilterParams: buildFullAlignFilterParams(saved)
				});
			}
		});
		localStorage.setItem(`${OKR_ALIGNMENT_ACTIVE_TAB_KEY}-${uData?.org_id}-${uData?.user_id}`, index); // save current tab to local store
	}

	renderTab(index, tab) {
		let title = tab.label || '';
		let activeClass = (this.state.activeTab == index) ? ' active ' : '';
		let id = tab.id + '-wbtab';
		return (
			<div className={"wbtabs-tab wbtabs-tab-alignment " + activeClass} id={id} onClick={e => this.tabClick(e, index, tab)} key={index}>
				<div style={{ height: "88%" }}>
					<span className="wbtabs-tab-title" style={{ color: 'black' }}>{title}</span>
				</div>
			</div>
		)
	}

	loadGoalAlignData(params = {}) {
		const { t } = this.props
		let _this = this;
		showLoaderWidget($("#content"));
		this.GoalServices.goalAlignObjective(params).then(function (response) {
			removeLoaderWidget();
			let responseData = response.data;
			if (Array.isArray(responseData.json) &&
        responseData.json.filter((item) => item?.key >= 0).length === 0) {
				toastr.info(`${t('There are no OKRs that match your selection.')} ${t('Please try a different filter.')}`);
			}
			sortTree(responseData.json);
			_this.setState({
				activeTab: "1",
				showPersonalObj: responseData.personalObj
			});
			mainGoal(
				responseData.json,
				"goal",
				responseData.peopleSearch,
				responseData.selectedUser,
				'',
				_this.renderGoalBox
			);
			_this.reopenNode(openAlignedIds);
		}).catch(err => {
			removeLoaderWidget(true);
			console.log('error:', err);
		});
	}

	expandSearchText(e) {
		e.preventDefault();
		this.setState({ expandText: true }, () => {
			this.refs.okr_search_text.focus();
		});
	}

	registerAutocomplete() {
		const { t } = this.props
		const DEBUG_SEARCH = false;
		let _this = this;
		//console.log('_this.props.searchList', _this.props.searchList);
		let searchList = _this.props.searchList || [];
		$("#okr_search_text")
			.autocomplete({
				minLength: 2,
				//source: searchList,
				source: function (request, response) {
					let params = {};
					var term = request.term;
					params.term = term;
					params.s = 1;
					if( _this.state.activeTab == "2" ) {
						params.refsrc = "Full_alignement_report_search_used";
					}
					if (_this.state.activeTab == 2) {
						$.ajax({
							url: wbConfig.baseUrl + "/goal/seachGoalAccessLists",
							type: "get",
							dataType: "json",
							data: params
						}).done(function (value) {
							var reports = [];
							$.map(value, function (item, key) {
								reports.push({
									label: item["label"],
									value: item["value"],
									id: item["id"],
									type: item["type"]
								});
							}),
							response(reports);
						});
					} else if (_this.state.activeTab == 1) {
						let params = { term: term, team: 1 };
						$.ajax({
							url: wbConfig.baseUrl + '/goal/searchUserListOrgLevel',
							type: 'get',
							dataType: "json",
							data: params
						})
							.done(function (value) {
								const { data = [] } = value;
								response(data);
							});
					} else if (_this.state.activeTab == 0) {
						let params = { term: term };
						if (_this.state.activeButton == 'button-teams') {
							params.onlyTeam = 1;
						}
						$.ajax({
							url: wbConfig.baseUrl + '/goal/searchUserListOrgLevel',
							type: 'get',
							dataType: "json",
							data: params
						})
						.done(function (value) {
							const { data = [] } = value;
							let reports = [];
							if (Array.isArray(data)) {
								reports = data.filter((item) =>
									((_this.state.activeButton == 'button-teams')
									|| (_this.state.activeButton == 'button-people'
										&& (item.isRoot || item.parentId))
									)
								)
							}
							response(reports);
						});
					}
				},

				select: function (event, ui) {
					if (_this.state.activeTab == '0') {
						if (_this.state.activeButton == 'button-teams') {
							if (ui.item.teamType == '2') {
								_this.GoalServices.getTeamOkrAlignment({ teamId: ui.item.id }).then((response) => {
									// render
									// console.log(response.data, 'working grop');
									if (response.data) {
										const data = response.data.data;
										let teamData = {
											type: 'team',
											id: data.teamId,
											title: data.teamName,
											subTitle: data.ownerName,
											viewTeamLabel: t('View working team'),
											items: data.goals || [],
											//    picture: wbConfig.baseUrl + '/images/team.PNG',
											useSpriteSheet: true,
											isPage: true
										};
										ReactDOM.render(<GoalBoxComp {...teamData} />, document.getElementById('goalAlign'));
									}
								});
							} else {
								setTimeout(function() {
									$(event.target).blur();
									$(event.target).val('');
								})
								ReactDOM.unmountComponentAtNode(document.getElementById('goalAlign'))
								let searchText = ui.item.id;
								if (_this.teamsData) {
									mainGoal(_this.teamsData, 'teams', [], _this.selectedTeam, searchText, _this.renderGoalBox);
								}
							}
						} else if (_this.state.activeButton == 'button-people') {
							setTimeout(function() {
								$(event.target).blur();
								$(event.target).val('');
							})
							let searchText = ui.item.userId;
							mainGoal(_this.popleJson, 'people', [], _this.selectedUser, searchText, _this.renderGoalBox);
						}
					}
					else if (_this.state.activeTab == '1') {
						showLoaderWidget($("#content"));
						let params = { chartType: chartType };
						const newTeamId = ui.item.type == 'team' ? ui.item.id : null;
						const newUserId = ui.item.type == 'user' ? ui.item.id : null;
						if (ui.item.type == 'team') {
							params.teamId = ui.item.id;
							_this.setState({ teamId: ui.item.id, userId: null });
						} else if (ui.item.type == 'user') {
							params.userId = ui.item.id;
							_this.setState({ userId: ui.item.id, teamId: null });
						}
						// Persist team/user so it is reused on next visit until user resets
						saveAlignmentFilters({ ...getAlignmentFilters(), teamId: newTeamId, userId: newUserId });
						_this.GoalServices.goalAlignObjective({ ...getAlignmentFilters(), ...params })
							.then(response => {
								$('#search_people').hide();
								$('#search_people').val("");
								removeLoaderWidget(true);
								if (response) {
									_this.setState({
										showPersonalObj: response.data.personalObj
									});
									var parseData = response.data;
									sortTree(parseData.json);
									mainGoal(parseData.json, 'goal', parseData.peopleSearch, parseData.selectedUser, '', _this.renderGoalBox);
								}
							});

					}
					else {
						// Full Alignment: persist team/user from search so it stays sticky when opening/applying filters
						const newTeamId = (ui.item.type === 'team') ? ui.item.id : null;
						const newUserId = (ui.item.type === 'user') ? ui.item.id : null;
						saveAlignmentFilters({ ...getAlignmentFilters(), teamId: newTeamId, userId: newUserId });
						const savedAfterSearch = getAlignmentFilters();
						_this.setState({
							triggerSearch: ui.item,
							teamId: newTeamId,
							userId: newUserId,
							fullAlignFilterParams: buildFullAlignFilterParams(savedAfterSearch, {
								teamId: newTeamId,
								userId: newUserId
							})
						});
					}
					$("#okr_search_text").val('');
					return false;
				},
				focus: function (event, ui) {
					return;
				},
				open: function (event, ui) {
					if (!$(".fullalign-autocomplete-ul li").length) {
						//$(".fullalign-autocomplete-ul").hide();
					} else {
						var autocomplete = $("#okr_search_text");
						var left = autocomplete.offset().left;
						var width = document.getElementById('searchButton').offsetWidth;
						$(".fullalign-autocomplete-ul").css("left", left);
						$(".fullalign-autocomplete-ul").css("width", width - 6);
					}
				},
				create: function () {
					$(this).data('ui-autocomplete')._renderItem = function (ul, item) {
						var imageDiv = '', detailsDiv;
						if (_this.state.activeTab != "2") {
							if (item.type && item.type === "team") {
								if (_this.state.activeTab != "0") {
									imageDiv = "<div class='row-image'><i class='icon icon-outline-people-24px' style='background-position: -1328px 0px;'></i></div>";
									detailsDiv = '<div class="autocomplete-row">' + imageDiv + '<div class="details"><div class="truncateText">' + item.label + '</div></div></div>';
								} else {
									detailsDiv = '<div class="autocomplete-row teamsonly"><div class="details"><div class="truncateText label">' + htmlEntities(item.label) + '</div><div class="truncateText owner-name">' + item.ownerName + '</div></div></div>';
								}
							} else {
								imageDiv = '<div class="row-image"><img class="roundFramePhoto fitProfileImage"' + '" src="' + item.userPic + '"></img></div>';
								detailsDiv = '<div class="autocomplete-row">' + imageDiv + '<div class="details"><div class="truncateText">' + item.label + '</div></div></div>';
							}
						} else {
							detailsDiv = '<div class="autocomplete-row teamsonly"><div class="details"><div class="truncateText">' + item.label + '</div></div></div>';
						}

						return $('<li class="loopAutoSuggest"></li>')
							.data("item.autocomplete", item)
							.append(detailsDiv)
							.appendTo(ul);
					};
				}
			})
			.autocomplete("widget")
			.addClass("fullalign-autocomplete-ul");
	}

	setExpandText(expandText) {
		this.setState({ expandText }, () => {
			if (expandText) {
				this.refs.okr_search_text.value = "";
			}
		});
	}

	closeHelp() {
		this.setState({ showHelpTextOnLoad: false })
	}

	clickPeopleAlign(e, userId = '') {
		e.preventDefault();
		if (userId) {
			this.loadGoalAlignData({ ...getAlignmentFilters(), userId });
		}
	}

	renderJTooltip() {
		const { t } = this.props;
		return (
			<div
				id="toolTip"
				className="tooltip goalAlign"
				style={{ opacity: 0, zIndex: 15 }}
			>
				<div id="goalPopUp">
					<div className="goalPopUpTop">
						<div className="goalPercentage">95%</div>
						<div className="goalName truncate3 goal-align-metric"> </div>
						<div className="clear" />
						<div className="goalPopUpOwnerPic">
							<img
								width="25"
								height="25"
								className="fitProfileImage roundFramePhoto"
								style={{ boxShadow: "0px 2px 4px #ccc" }}
								src={this.props.userData.picture}
							/>
						</div>
						<div className="goalPopUpOwner">
							<div className="goalPopUpOwnerName truncateText">
								{t('Jose Roberto')}
              </div>
							<div className="goalPopUpOwnerNameTitle truncateText">
								{t('Operations')}
              </div>
						</div>
					</div>
					<div className="clear" />
					<div className="goalPopUpMetric" />
				</div>
				<div id="peopleTreePopUp" style={{ display: "none" }}>

				</div>
				<div className="tooltipTail" />
			</div>
		);
	}

	addExistingGoal(e) {
		e.preventDefault();
		$("#addExistingGoalPopup").hide();
		var goalId =
			$(e.target).attr("data-goal-parent-id") ||
			$(e.target)
				.closest(".addExistingGoal")
				.attr("data-goal-parent-id");
		$(".userAlignGoalList").attr("data-goal-parent-id", goalId);
		$(".userAlignGoalList")
			.show()
			.scrollTop(0);
		$('.userAlignGoalListData').liveFilterData('.goalSearchboxInput', 'div.goalListData', {
				filterChildSelector: 'div.goalListDataName'
			  });
		$(".goalSearchboxInput")
			.val("")
			.trigger("keyup");
	}

	addNewGoal(e) {
		e.preventDefault();
		var goalId = $(e.target).attr("data-goal-parent-id");
		$(
			".setNewGoalPopup .setNewGoalIndPopup,.setNewGoalPopup .setNewGoalTeamPopup"
		).attr("data-goal-parent-id", goalId);
		$(".setNewGoalPopup").show();
		$('#addExistingGoalPopup').hide();
	}

	showAddObjectives() {
		const { t } = this.props;
		return (
			<div
				className="wspopupmenu-wrapper addExistingGoalPopup "
				id="addExistingGoalPopup"
			>
				<div
					className="wspopupmenu-list clickable addExistingGoal"
					data-goal-parent-id=""
					onClick={e => this.addExistingGoal(e)}
				>
					<img
						style={{ float: "left" }}
						className="dropdown-icon"
						src={
							wbConfig.imageCdnUrl + "/images/icons/menu/icons_goals_grey.png"
						}
						hsrc={wbConfig.imageCdnUrl + "/images/goal/goals_blue_icon.png"}
					/>
					<div style={{ paddingRight: "5px", paddingLeft: "28px" }}>
					{t('Add existing Objective')}
          </div>
				</div>
				<div
					className="wspopupmenu-list clickable addNewGoal"
					data-goal-parent-id=""
					onClick={e => this.addNewGoal(e)}
				>
					<img
						style={{ paddingRight: "12px !important" }}
						className="dropdown-icon"
						src={wbConfig.imageCdnUrl + "/images/icons/main_menu/add_ai.png"}
						hsrc={
							wbConfig.imageCdnUrl + "/images/icons/dropdowns/add_selected.png"
						}
					/>
          {t('Add new Objective')}
        </div>
			</div>
		);
	}

	setTeamObjectives() {
		let personalObj = this.state.showPersonalObj;
		const { t } = this.props;
		return (
			<div className="wspopupmenu-wrapper setNewGoalPopup ">
				{personalObj &&
					<div
						className="wspopupmenu-list clickable setNewGoalIndPopup"
						data-goal-parent-id=""
						mygoal="1"
					>
						<img
							style={{ float: "left" }}
							className="dropdown-icon"
							src={wbConfig.imageCdnUrl + "/images/icons/main_menu/add_ai.png"}
							hsrc={
								wbConfig.imageCdnUrl + "/images/icons/dropdowns/add_selected.png"
							}
						/>
						<div style={{ paddingRight: "5px", paddingLeft: "28px" }}>
						{t('Set individual objective')}
            </div>
					</div>
				}
				<div
					className="wspopupmenu-list clickable setNewGoalTeamPopup"
					data-goal-parent-id=""
				>
					<img
						style={{ paddingRight: "12px !important" }}
						className="dropdown-icon"
						src={wbConfig.imageCdnUrl + "/images/icons/main_menu/add_ai.png"}
						hsrc={
							wbConfig.imageCdnUrl + "/images/icons/dropdowns/add_selected.png"
						}
					/>
					<div style={{ paddingRight: "5px", paddingLeft: "28px" }}>
					{t('Set team objective')}
          </div>
				</div>
			</div>
		);
	}

	goalListData(e, u) {
		e.preventDefault();
		//console.log('goalListData', u);
		$(".userAlignGoalList").hide();
		var parentId = $(".userAlignGoalList").attr("data-goal-parent-id");
		var goalId = u.id;
		showLoaderWidget($("#content"));
		let _this = this;
		this.GoalServices.goalAlignObjective({
			userId: this.props.selectedUser.userId,
			dependencies: 1,
			goalId: goalId,
			parentId: parentId
		}).then(function (response) {
			removeLoaderWidget(true);
			let responseData = response.data;
			sortTree(responseData.json);
			mainGoal(
				responseData.json,
				"goal",
				responseData.peopleSearch,
				responseData.selectedUser,
				'',
				_this.renderGoalBox
			);
			var openAlignedIds = [];
			if ($(".childOpen").length) {
				for (var i = 1; i < 1000; i++) {
					var attr = $(".childOpen").attr("openId_" + i);
					if (typeof attr !== "undefined" && attr != -1) {
						openAlignedIds.push(attr);
					} else {
						break;
					}
				}
			}
			var _openAlignedIds = openAlignedIds.toString();
			_this.reopenNode(_openAlignedIds);
		});
	}

	renderUserGoals() {
		let { userMyGoals = [] } = this.props;
		const { t } = this.props
		return (
			<div className="userAlignGoalList" style={{ display: "none" }}>
				<div className="goalSearchbox">
					<input
						type="text"
						className="goalSearchboxInput"
						placeholder={t('Search for a goal')}
					/>
				</div>
				<div className="userAlignGoalListData">
					{userMyGoals.map(u => (
						<div
							className="goalListData"
							data-goalid={u.id}
							id={"goalListData_" + u.id}
							onClick={e => this.goalListData(e, u)}
							key={"user_align_goal_list_data_" + u.id}
						>
							<div className="goalListDataName">{u.goal}</div>
							<div
								className="goalListDataPer"
								style={{ color: u.progressColor }}
							>
								{u.goalPercentage}%
              </div>
						</div>
					))}
				</div>
			</div>
		);
	}

	hideSearchPeople(e) {
		var ele = document.querySelector("#" + e.target.id);
		if (ele) {
			ele.style.display = "none";
		}
	}

	// Set styles for d3 trees
	setVizStyles(enable) {
		// Floating footer
		let elem = document.querySelector('div#footer');
		if (elem) {
			const isIE = navigator.userAgent.indexOf("MSIE ") > -1 || navigator.userAgent.indexOf("Trident/") > -1;
			if (isIE) {
				elem.style.display = enable ? 'none' : null;
			} else {
				if (enable) {
					elem.classList.add('fixedbottom-footer');
				} else {
					elem.classList.remove('fixedbottom-footer');
				}
			}
		}
	}

	render() {
		const { t } = this.props;
		const filterCount = getAlignmentFiltersCount();
		let filterValue = '1';
		this.setVizStyles(this.state.activeTab == 0 || this.state.activeTab == 1);
		return (
			<div className="okr-alignment-wrapper" style={{ maxWidth: "1200px" }}>
				<div className="okr-alignment-header-wrapper left">
					<div
						className="left okr-alignment-title"
						role="heading"
						aria-level="1"
            			id="main-heading"
					>
						{t('OKR Alignment')}
					</div>
					<div className="clear" />
				</div>
				<div className="okr-share-buttons right">
					{this.state.activeTab == "2" &&
						this.state.showPubisher ? (
							<PublisherComp
								printUrl="goal/goalAlignPrint"
								getPrintQueryParams={this.getAlignmentPrintQueryParams}
								emailSendUrl="goal/sendAlignGoal"
								showSlack={this.props.isSlackEnabled}
								publishSlackUrl="integration/goalAlighnPublish"
							/>
						) : (
							""
						)}
				</div>
				<div className="wbtabs-wrapper">
					<div className="wbtabs" style={{ "paddingTop": "20px", "marginLeft": "-20px" }} >
						{this.tabs.map((t, i) => this.renderTab(i, t))}
					</div>
				</div>
				<div className="clear" />
				<div style={{ padding: "15px 20px 0px" }}>
					<div className="width100" style={{ 'display': 'flex' }}>
						{/* For buttons comp additonal check to be added
            only show them when there is teams data available */}
						{this.state.activeTab == "0" && this.teams ? <ButtonsComp buttons={this.getButtons()} /> : ''}
						{this.state.activeTab == "2" ? <div className="show-me-fullAlign">{t('Show me')}:</div> : ''}
						<div
							className="okr-alignment-button"
							style={{
									width: this.state.activeTab == "0" && this.teams ? "25%" : "40%",
									minWidth: this.state.activeTab == "0" && this.teams ? "200px" : "260px"
								}}>
							<div
								className="searchButton" id='searchButton'>
								<div
									className="okr-search-icon"
									onClick={e => this.expandSearchText(e)} />
								<input
									className="okr-search-text"
									ref="okr_search_text"
									id="okr_search_text"
									style={{ display: "block" }}
                  type="text"
                  onChange={() => this.registerAutocomplete()}
									placeholder={this.state.activeTab != '0' ? t("Search for a person or team to display") :
										this.state.activeButton == 'button-teams' ? t("Search for a team to display") : t("Search for a person to display")} />
							</div>
						</div>
						{this.state.activeTab != "2" ?
							<div className="okr-align-help-wrapper left">
								<HelpTextComp
									wrapperClass='helptextcomp-align-wrapper'
									autoOpen={this.state.showOneTimeMessage}
								>
									<div
										className='h1title'
									>
										{t('How to use tree chart')}
                      {
											this.state.showOneTimeMessage ?
												<span id="close-button" onClick={() => this.closeOneTimeMessage()}
													style={{
														opacity: "0.8",
														float: "right"
													}}>
													<i className="icon icon-outline-close-24px" />
												</span>
												: ''
										}
									</div>
									<div className="width100">
										<ul>
											<li><b>1.</b>  {t('Hover over names to show OKRs below each item')}</li>
											<li><b>2.</b>  {t('You can move the tree around by clicking and dragging')}</li>
											<li><b>3.</b>  {t('Clicking on the larger nodes will open up the next layer')}</li>
										</ul>
									</div>
								</HelpTextComp>
							</div> : ''}
              {(this.state.activeTab == '1' || this.state.activeTab == '2')
                ? <div
                  className="right"
                  style={{
                    zIndex: 1,
                    marginLeft: 'auto'
                  }}
                >
                  {filterCount > 0 && (
                    <span
                      className="alignmentCountFilter"
                      aria-hidden="true"
                      style={{ marginRight: '6px' }}
                    >
                      {filterCount}
                    </span>
                  )}

                  <button
                    className="alignment-btn-nav-week rightEdgeEltooltip"
										ref={this.filterButtonRef}
                    onClick={this.openAlignmentFilter}
                    id="filterDate"
                    title={t("Filter Options")}
                    aria-label={t("Filter Options")}
                  >
                    <span className="icon icon-outline-filter_list-24px" />
                  </button>
                  </div>
                : ''}
            <div
              className="calendar-filter right"
              style={{
                paddingLeft: this.props.space ? '30px' : '0px',
                marginLeft: '10px',
                // zIndex: 100
              }}
            >
              <AlignmentFilter
                anchorEl={this.filterButtonRef.current}
                loadGoalAlignCallback={this.handleFilterApply}
                filterDateArr={this.state.filterDateArray}
                open={this.state.openAlignmentFilter}
                close={this.openAlignmentFilter}
                teamId={this.state.teamId}
                userId={this.state.userId}
                alignmentFilters={getAlignmentFilters()}
                saveAlignmentFilters={saveAlignmentFilters}
              />
            </div>
						<div className="clear" />
					</div>
				</div>
				<div className="width100 okr-alignment-content-wrapper">
					<div className="goalpageRender goalAlignRender dropdownAlign" style={{"marginTop": this.state.activeTab != "2" ? "-54px" : ""}}>
						{this.state.activeTab == "2" ? (
							<OkrAlignmentTable
								ref={this.OkrAlignmentTable}
								wrapperClass="okr-alignment-view-wrapper"
								filterValue={filterValue}
								from="okr-alignment"
								userId={this.selectedUserId}
								triggerSearch={this.state.triggerSearch}
								setTriggerSearch={triggerSearch => this.setState({ triggerSearch })}
								registerAutocomplete={this.registerAutocomplete}
								setExpandText={this.setExpandText}
								setShowPublisher={(showPubisher) => this.setState({ showPubisher })}
								filterParams={this.state.fullAlignFilterParams}
							/>
						) : (
								<div
									className="width100"
									id="goalAlign-wrap"
									style={{
										left: "var(--leftMenuWidth)"
									}}
								>
									<div
										id="goalAlign"
										style={{
											position: "absolute",
										}}
									/>
									{/* <input
                  type="text"
                  id="search_people"
                  style={{ display: "none" }}
                  placeholder="Search for people"
                  onBlur={e => this.hideSearchPeople(e)}
                /> */}
								</div>
							)}
					</div>
				</div>
				{this.renderJTooltip()}
				{this.showAddObjectives()}
				{this.setTeamObjectives()}
				{this.renderUserGoals()}
				<div className="userAlignGoalEmpty">
					{t('To best harness the power of the goal flow')}
          <br />
          {t('visualization')},{" "}
					<span className="setGoal setNewGoalTeamPopup">{t('set an objective')}</span>.
        </div>
				<div className="childOpen" style={{ display: "none" }} />
				<div className="clear" />
			</div>
		);
	}
}

const enhance = compose(withTranslation(), hot);
export default enhance(OkrAlignmentComp);
